import { useEffect, useState, useRef, useMemo, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sun,
  Moon,
  Desktop,
  User,
  PaintBrush,
  Keyboard,
  Plugs,
  Key,
  Lightning,
  Check,
  Trash,
  X,
  EnvelopeSimple,
  CalendarBlank,
  CloudArrowUp,
  CircleNotch,
  Bell,
  ArrowsClockwise,
  Cpu,
  CursorText,
  CaretDown,
  Command,
  ArrowRight,
  ArrowFatUp,
} from "@phosphor-icons/react";
import { cn } from "../../lib/cn";
import {
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  DropdownSeparator,
  DropdownLabel,
} from "../../components/ui/Dropdown";
import { useUIStore, type Theme, type ToastStyle, type SelectionToolbarAnchor } from "../../stores/ui";
import { SYNTAX_THEMES, type SyntaxTheme } from "../../theme/syntax-themes";
import { useAuthStore } from "../../stores/auth";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import { useToast } from "../../components/ui/Toast";
import {
  useCompletionStore,
  type CompletionAggressiveness,
  type CompletionLength,
  type CompletionTrigger,
} from "../../stores/completion";
import {
  useIntegrationsStore,
  type IntegrationProvider as IntProvider,
} from "../../stores/integrations";
import { useUserAIKeysStore } from "../../stores/userAIKeys";
import { useAIStore } from "../../stores/ai";
import { useCitationStore } from "../../stores/citation";
import { BookOpen, ArrowSquareOut } from "@phosphor-icons/react";

const spring = { type: "spring" as const, stiffness: 400, damping: 32, mass: 0.8 };

const AI_PROVIDERS = [
  { id: "openai", label: "OpenAI", hint: "sk-..." },
  { id: "anthropic", label: "Anthropic", hint: "sk-ant-..." },
  { id: "gemini", label: "Gemini", hint: "AI..." },
  { id: "xai", label: "xAI", hint: "xai-..." },
  { id: "cerebras", label: "Cerebras", hint: "csk-..." },
  { id: "groq", label: "Groq", hint: "gsk_..." },
];

const THEME_OPTIONS: { id: Theme; label: string; description: string; icon: typeof Sun }[] = [
  { id: "light", label: "Light", description: "Always use light colors", icon: Sun },
  { id: "dark", label: "Dark", description: "Always use dark colors", icon: Moon },
  { id: "system", label: "System", description: "Match your OS preference", icon: Desktop },
];

function ThemeCard({ opt, active, onSelect }: { opt: (typeof THEME_OPTIONS)[number]; active: boolean; onSelect: () => void }) {
  const Icon = opt.icon;
  return (
    <motion.button
      onClick={onSelect}
      whileHover={{ y: -1 }}
      whileTap={{ scale: 0.98 }}
      transition={{ type: "spring", stiffness: 500, damping: 35 }}
      className={cn(
        "relative flex flex-col items-center gap-2 rounded-[var(--radius-lg)] p-4 text-center",
        "border transition-colors duration-100",
        active
          ? "border-[var(--color-lever-orange)] bg-[var(--bg-surface)] shadow-[var(--shadow-sm)]"
          : "border-[var(--border-hairline)] bg-[var(--bg-surface)] hover:border-[var(--border-subtle)]",
      )}
    >
      <div className={cn(
        "flex h-10 w-10 items-center justify-center rounded-full",
        active ? "bg-[var(--color-lever-orange)]/10 text-[var(--color-lever-orange)]" : "bg-[var(--bg-base)] text-[var(--text-muted)]",
      )}>
        <Icon size={20} weight={active ? "fill" : "regular"} />
      </div>
      <div>
        <div className="text-[13px] font-semibold text-[var(--text-primary)]">{opt.label}</div>
        <div className="text-[11px] text-[var(--text-muted)] mt-0.5">{opt.description}</div>
      </div>
      {active && (
        <motion.div
          layoutId="theme-active-ring"
          className="absolute inset-0 rounded-[var(--radius-lg)] ring-2 ring-[var(--color-lever-orange)]/30"
          transition={spring}
        />
      )}
    </motion.button>
  );
}

const KbdCmd = <Command size={11} weight="bold" />;
const KbdShift = <ArrowFatUp size={11} weight="bold" />;
const KbdRight = <ArrowRight size={11} weight="bold" />;

function buildSettingsShortcutGroups(): {
  title: string;
  items: { keys: ReactNode[]; label: string }[];
}[] {
  const isMac = typeof navigator !== "undefined" && /Mac/i.test(navigator.userAgent);
  const mod = isMac ? KbdCmd : "Ctrl";
  return [
    {
      title: "Global",
      items: [
        { keys: [mod, "K"], label: "Search" },
        { keys: [mod, "N"], label: "New project" },
        { keys: [mod, ","], label: "Settings" },
        { keys: ["Esc"], label: "Close dialog" },
      ],
    },
    {
      title: "Editor",
      items: [{ keys: [mod, "G"], label: "Inline assistant" }],
    },
  ];
}

const AGGRESSIVENESS_OPTIONS: { id: CompletionAggressiveness; label: string; description: string }[] = [
  { id: "conservative", label: "Conservative", description: "1.2s delay, fewer suggestions" },
  { id: "balanced", label: "Balanced", description: "0.6s delay, default" },
  { id: "aggressive", label: "Aggressive", description: "0.3s delay, more frequent" },
];

const LENGTH_OPTIONS: { id: CompletionLength; label: string; description: string }[] = [
  { id: "short", label: "Short", description: "~1 sentence" },
  { id: "medium", label: "Medium", description: "~1 paragraph" },
  { id: "long", label: "Long", description: "Multi-paragraph" },
];

const TRIGGER_OPTIONS: { id: CompletionTrigger; label: string; description: string }[] = [
  { id: "pause", label: "On pause", description: "After you stop typing" },
  { id: "newline", label: "On newline", description: "When pressing Enter" },
  { id: "manual", label: "Manual", description: "Keybind only" },
];

const TOAST_STYLE_OPTIONS: { id: ToastStyle; label: string; description: string }[] = [
  { id: "normal", label: "Normal", description: "Full toast with details" },
  { id: "minimal", label: "Minimal", description: "Compact, fades faster" },
];

const TOOLBAR_ANCHOR_OPTIONS: { id: SelectionToolbarAnchor; label: string; description: string }[] = [
  { id: "start", label: "Start", description: "Above the selection" },
  { id: "end", label: "End", description: "Below the selection" },
];

function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { id: T; label: string; description: string }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="flex gap-1.5">
      {options.map((opt) => (
        <motion.button
          key={opt.id}
          onClick={() => onChange(opt.id)}
          whileTap={{ scale: 0.97 }}
          className={cn(
            "flex-1 rounded-[var(--radius-md)] px-3 py-2 text-center transition-colors duration-100",
            "border",
            value === opt.id
              ? "border-[var(--color-lever-orange)] bg-[var(--color-lever-orange)]/8 text-[var(--text-primary)]"
              : "border-[var(--border-hairline)] bg-[var(--bg-surface)] text-[var(--text-muted)] hover:border-[var(--border-subtle)] hover:text-[var(--text-secondary)]",
          )}
        >
          <div className="text-[12px] font-medium">{opt.label}</div>
          <div className="text-[10px] opacity-60 mt-0.5">{opt.description}</div>
        </motion.button>
      ))}
    </div>
  );
}

const PROVIDER_LABELS: Record<string, string> = {
  gemini: "Gemini",
  xai: "Grok",
  cerebras: "Cerebras",
  groq: "Groq",
  openai: "OpenAI",
  anthropic: "Anthropic",
};

function CompletionModelPicker({
  providers,
  completionProvider,
  completionModel,
  onSelect,
  onClear,
}: {
  providers: { id: string; name: string; models: { id: string; name: string; tier: string }[] }[];
  completionProvider: string | null;
  completionModel: string | null;
  onSelect: (providerId: string, modelId: string) => void;
  onClear: () => void;
}) {
  const activeLabel = (() => {
    if (!completionProvider || !completionModel) return "Same as primary model";
    for (const p of providers) {
      const m = p.models.find((m) => m.id === completionModel);
      if (m) return `${PROVIDER_LABELS[p.id] ?? p.name} — ${m.name}`;
    }
    return completionModel;
  })();

  const hasSelection = !!(completionProvider && completionModel);

  return (
    <Dropdown>
      <DropdownTrigger
        className={cn(
          "flex w-full items-center justify-between",
          "rounded-[var(--radius-md)] border px-3 py-2",
          "text-[12px] text-left",
          "transition-colors duration-[var(--duration-fast)]",
          hasSelection
            ? "border-[var(--border-subtle)] bg-[var(--bg-base)] text-[var(--text-primary)]"
            : "border-[var(--border-hairline)] bg-[var(--bg-base)] text-[var(--text-muted)]",
          "hover:border-[var(--border-subtle)]",
        )}
      >
        <span className="truncate">{activeLabel}</span>
        <CaretDown size={11} weight="bold" className="shrink-0 ml-2 opacity-40" />
      </DropdownTrigger>
      <DropdownMenu className="w-72">
        <DropdownItem
          onClick={onClear}
          className="justify-between"
        >
          <span className={cn(!hasSelection ? "text-[var(--text-primary)]" : "text-[var(--text-secondary)]")}>
            Same as primary model
          </span>
          {!hasSelection && (
            <Check size={13} weight="bold" className="text-[var(--color-signal-green)]" />
          )}
        </DropdownItem>
        {providers.map((provider, i) => (
          <div key={provider.id}>
            {i === 0 && <DropdownSeparator />}
            <DropdownLabel>{PROVIDER_LABELS[provider.id] ?? provider.name}</DropdownLabel>
            {provider.models.map((model) => {
              const isActive = completionProvider === provider.id && completionModel === model.id;
              return (
                <DropdownItem
                  key={model.id}
                  onClick={() => onSelect(provider.id, model.id)}
                  className="justify-between"
                >
                  <span className="flex items-center gap-2">
                    <span className={cn(isActive ? "text-[var(--text-primary)]" : "text-[var(--text-secondary)]")}>
                      {model.name}
                    </span>
                    <span
                      className={cn(
                        "inline-flex items-center rounded-full px-1.5 py-0.5",
                        "text-[9px] font-semibold uppercase tracking-wider leading-none",
                        model.tier === "free"
                          ? "bg-[var(--color-signal-green)]/10 text-[var(--color-signal-green)]"
                          : "bg-[var(--color-ember-orange)]/10 text-[var(--color-ember-orange)]",
                      )}
                    >
                      {model.tier}
                    </span>
                  </span>
                  {isActive && (
                    <Check size={13} weight="bold" className="text-[var(--color-signal-green)]" />
                  )}
                </DropdownItem>
              );
            })}
            {i < providers.length - 1 && <DropdownSeparator />}
          </div>
        ))}
      </DropdownMenu>
    </Dropdown>
  );
}

export function SettingsPage() {
  useDocumentTitle("Settings");
  const { user, devMode } = useAuthStore();
  const theme = useUIStore((s) => s.theme);
  const setTheme = useUIStore((s) => s.setTheme);
  const editorTheme = useUIStore((s) => s.editorTheme);
  const setEditorTheme = useUIStore((s) => s.setEditorTheme);
  const toastStyle = useUIStore((s) => s.toastStyle);
  const setToastStyle = useUIStore((s) => s.setToastStyle);
  const toolbarAnchor = useUIStore((s) => s.selectionToolbarAnchor);
  const setToolbarAnchor = useUIStore((s) => s.setSelectionToolbarAnchor);

  const completionEnabled = useCompletionStore((s) => s.enabled);
  const completionAggressiveness = useCompletionStore((s) => s.aggressiveness);
  const completionMaxLength = useCompletionStore((s) => s.maxLength);
  const completionTrigger = useCompletionStore((s) => s.trigger);
  const setCompletionEnabled = useCompletionStore((s) => s.setEnabled);
  const setCompletionAggressiveness = useCompletionStore((s) => s.setAggressiveness);
  const setCompletionMaxLength = useCompletionStore((s) => s.setMaxLength);
  const setCompletionTrigger = useCompletionStore((s) => s.setTrigger);

  const integrations = useIntegrationsStore((s) => s.integrations);
  const fetchIntegrations = useIntegrationsStore((s) => s.fetchIntegrations);
  const startOAuth = useIntegrationsStore((s) => s.startOAuth);
  const disconnectIntegration = useIntegrationsStore((s) => s.disconnect);
  const handleOAuthCallback = useIntegrationsStore((s) => s.handleOAuthCallback);
  const integrationsLoading = useIntegrationsStore((s) => s.loading);

  const aiKeys = useUserAIKeysStore((s) => s.keys);
  const aiKeysSaving = useUserAIKeysStore((s) => s.saving);
  const fetchAIKeys = useUserAIKeysStore((s) => s.fetchKeys);
  const saveAIKey = useUserAIKeysStore((s) => s.saveKey);
  const deleteAIKey = useUserAIKeysStore((s) => s.deleteKey);

  const availableProviders = useAIStore((s) => s.availableProviders);
  const fetchModels = useAIStore((s) => s.fetchModels);
  const selectedProvider = useAIStore((s) => s.selectedProvider);
  const selectedModel = useAIStore((s) => s.selectedModel);
  const setProvider = useAIStore((s) => s.setProvider);
  const completionProvider = useAIStore((s) => s.completionProvider);
  const completionModel = useAIStore((s) => s.completionModel);
  const setCompletionProvider = useAIStore((s) => s.setCompletionProvider);
  const clearCompletionProvider = useAIStore((s) => s.clearCompletionProvider);

  const s2Key = useCitationStore((s) => s.semanticScholarApiKey);
  const setS2Key = useCitationStore((s) => s.setSemanticScholarApiKey);

  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [disconnecting, setDisconnecting] = useState<string | null>(null);
  const [editingProvider, setEditingProvider] = useState<string | null>(null);
  const [keyInput, setKeyInput] = useState("");
  const [s2KeyInput, setS2KeyInput] = useState("");
  const [editingS2Key, setEditingS2Key] = useState(false);
  const keyInputRef = useRef<HTMLInputElement>(null);
  const s2KeyInputRef = useRef<HTMLInputElement>(null);
  const oauthHandled = useRef(false);

  const shortcutGroups = useMemo(() => buildSettingsShortcutGroups(), []);

  useEffect(() => {
    fetchIntegrations();
    fetchAIKeys();
    if (availableProviders.length === 0) fetchModels();
  }, [fetchIntegrations, fetchAIKeys, availableProviders.length, fetchModels]);

  useEffect(() => {
    const code = searchParams.get("code");
    const provider = searchParams.get("integration_callback");
    if (code && provider && !oauthHandled.current) {
      oauthHandled.current = true;
      handleOAuthCallback(code, provider as IntProvider)
        .then(() => {
          toast.success("Connected", `${provider} integration connected successfully.`);
          fetchIntegrations();
        })
        .catch(() => {
          toast.error("Connection failed", `Could not complete ${provider} OAuth.`);
        })
        .finally(() => {
          setSearchParams({}, { replace: true });
        });
    }
  }, [searchParams, handleOAuthCallback, fetchIntegrations, toast, setSearchParams]);

  const handleDisconnect = async (provider: IntProvider) => {
    setDisconnecting(provider);
    try {
      await disconnectIntegration(provider);
      toast.success("Disconnected", `${provider} integration removed.`);
    } catch {
      toast.error("Disconnect failed", `Could not disconnect ${provider}.`);
    } finally {
      setDisconnecting(null);
    }
  };

  const handleConnect = async () => {
    try {
      await startOAuth();
    } catch {
      toast.error("Connection failed", "Could not start OAuth flow.");
    }
  };

  const displayName = user?.user_metadata?.name || user?.email?.split("@")[0] || (devMode ? "Developer" : "User");
  const displayEmail = user?.email || (devMode ? "dev@lever.local" : "");

  return (
    <div className="p-4 pb-8 max-w-2xl mx-auto">
      <h1
        className="text-[17px] font-semibold text-[var(--text-primary)] tracking-[-0.03em]"
        style={{ fontFamily: "var(--font-display)" }}
      >
        Settings
      </h1>
      <p className="text-[11px] text-[var(--text-muted)] mb-6">Manage your account and preferences</p>

      {/* Profile section */}
      <section className="mb-8">
        <div className="flex items-center gap-2 mb-3">
          <User size={14} className="text-[var(--text-muted)]" />
          <h2 className="text-[13px] font-semibold text-[var(--text-primary)]">Profile</h2>
        </div>
        <div className="rounded-[var(--radius-lg)] border border-[var(--border-hairline)] bg-[var(--bg-surface)] p-4">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--text-primary)] text-[16px] font-bold text-[var(--text-inverse)]">
              {displayName.charAt(0).toUpperCase()}
            </div>
            <div>
              <div className="text-[14px] font-semibold text-[var(--text-primary)]">{displayName}</div>
              {displayEmail && <div className="text-[12px] text-[var(--text-muted)]">{displayEmail}</div>}
            </div>
          </div>
        </div>
      </section>

      {/* Appearance section */}
      <section className="mb-8">
        <div className="flex items-center gap-2 mb-3">
          <PaintBrush size={14} className="text-[var(--text-muted)]" />
          <h2 className="text-[13px] font-semibold text-[var(--text-primary)]">Appearance</h2>
        </div>
        <div className="grid grid-cols-3 gap-2.5">
          {THEME_OPTIONS.map((opt) => (
            <ThemeCard
              key={opt.id}
              opt={opt}
              active={theme === opt.id}
              onSelect={() => setTheme(opt.id)}
            />
          ))}
        </div>
      </section>

      {/* Notifications section */}
      <section className="mb-8">
        <div className="flex items-center gap-2 mb-3">
          <Bell size={14} className="text-[var(--text-muted)]" />
          <h2 className="text-[13px] font-semibold text-[var(--text-primary)]">Notifications</h2>
        </div>
        <div className="rounded-[var(--radius-lg)] border border-[var(--border-hairline)] bg-[var(--bg-surface)] p-4 space-y-4">
          <div>
            <div className="text-[12px] font-medium text-[var(--text-secondary)] mb-2">Toast style</div>
            <SegmentedControl
              options={TOAST_STYLE_OPTIONS}
              value={toastStyle}
              onChange={setToastStyle}
            />
          </div>
        </div>
      </section>

      {/* Editor Theme section */}
      <section className="mb-8">
        <div className="flex items-center gap-2 mb-3">
          <PaintBrush size={14} className="text-[var(--text-muted)]" />
          <h2 className="text-[13px] font-semibold text-[var(--text-primary)]">Editor Theme</h2>
        </div>
        <p className="text-[11px] text-[var(--text-muted)] mb-3">
          Syntax highlighting colors for the code editor.
        </p>
        <div className="grid grid-cols-5 gap-2">
          {SYNTAX_THEMES.map((st: SyntaxTheme) => {
            const active = editorTheme === st.id;
            const resolvedMode = theme === "system"
              ? (window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light")
              : (theme === "dark" ? "dark" : "light");
            const colors = st[resolvedMode];
            return (
              <motion.button
                key={st.id}
                onClick={() => setEditorTheme(st.id)}
                whileHover={{ y: -1 }}
                whileTap={{ scale: 0.97 }}
                transition={spring}
                className={cn(
                  "flex flex-col items-center gap-1.5 rounded-[var(--radius-lg)] p-3 text-center",
                  "border transition-colors duration-100",
                  active
                    ? "border-[var(--color-lever-orange)] bg-[var(--bg-surface)] shadow-[var(--shadow-sm)]"
                    : "border-[var(--border-hairline)] bg-[var(--bg-surface)] hover:border-[var(--border-subtle)]",
                )}
              >
                <div className="flex gap-[3px]">
                  {[colors.command, colors.string, colors.number, colors.math, colors.comment].map(
                    (c, i) => (
                      <div
                        key={i}
                        className="h-3 w-3 rounded-full"
                        style={{ backgroundColor: c }}
                      />
                    ),
                  )}
                </div>
                <span className="text-[11px] font-medium text-[var(--text-primary)]">
                  {st.name}
                </span>
                {active && (
                  <motion.div
                    layoutId="editorThemeCheck"
                    transition={spring}
                    className="absolute top-1.5 right-1.5"
                  >
                    <Check size={10} weight="bold" className="text-[var(--color-lever-orange)]" />
                  </motion.div>
                )}
              </motion.button>
            );
          })}
        </div>
      </section>

      {/* Editor section */}
      <section className="mb-8">
        <div className="flex items-center gap-2 mb-3">
          <CursorText size={14} className="text-[var(--text-muted)]" />
          <h2 className="text-[13px] font-semibold text-[var(--text-primary)]">Editor</h2>
        </div>
        <div className="rounded-[var(--radius-lg)] border border-[var(--border-hairline)] bg-[var(--bg-surface)] p-4 space-y-4">
          <div>
            <div className="text-[12px] font-medium text-[var(--text-secondary)] mb-1">Selection toolbar position</div>
            <p className="text-[10px] text-[var(--text-muted)] mb-2">
              Show the action toolbar above the start of your selection or below the end.
            </p>
            <SegmentedControl
              options={TOOLBAR_ANCHOR_OPTIONS}
              value={toolbarAnchor}
              onChange={setToolbarAnchor}
            />
          </div>
        </div>
      </section>

      {/* Integrations section */}
      <section className="mb-8">
        <div className="flex items-center gap-2 mb-3">
          <Plugs size={14} className="text-[var(--text-muted)]" />
          <h2 className="text-[13px] font-semibold text-[var(--text-primary)]">Integrations</h2>
        </div>
        {integrationsLoading && integrations.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <CircleNotch size={16} weight="bold" className="animate-spin text-[var(--text-muted)]" />
          </div>
        ) : (
          <div className="rounded-[var(--radius-lg)] border border-[var(--border-hairline)] bg-[var(--bg-surface)] divide-y divide-[var(--border-hairline)]">
            {([
              { provider: "gmail" as IntProvider, label: "Gmail", description: "Send and import emails", Icon: EnvelopeSimple },
              { provider: "calendar" as IntProvider, label: "Google Calendar", description: "Meeting notes and schedules", Icon: CalendarBlank },
              { provider: "drive" as IntProvider, label: "Google Drive", description: "Sync and import files", Icon: CloudArrowUp },
            ]).map(({ provider, label, description, Icon }) => {
              const status = integrations.find((i) => i.provider === provider);
              const connected = status?.connected ?? false;
              const isDisconnecting = disconnecting === provider;
              return (
                <div key={provider} className="flex items-center justify-between px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "flex h-8 w-8 items-center justify-center rounded-[var(--radius-md)]",
                      connected ? "bg-[var(--color-signal-green)]/10 text-[var(--color-signal-green)]" : "bg-[var(--bg-base)] text-[var(--text-muted)]",
                    )}>
                      <Icon size={16} weight={connected ? "fill" : "regular"} />
                    </div>
                    <div>
                      <div className="text-[13px] font-medium text-[var(--text-primary)]">{label}</div>
                      <div className="text-[11px] text-[var(--text-muted)]">
                        {connected ? status?.account_email ?? "Connected" : description}
                      </div>
                    </div>
                  </div>
                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    disabled={isDisconnecting}
                    onClick={() => connected ? handleDisconnect(provider) : handleConnect()}
                    className={cn(
                      "rounded-[var(--radius-md)] px-3 py-1.5 text-[11px] font-medium transition-colors disabled:opacity-50",
                      connected
                        ? "border border-[var(--border-subtle)] text-[var(--text-muted)] hover:text-[var(--color-soft-red)] hover:border-[var(--color-soft-red)]"
                        : "bg-[var(--color-lever-orange)] text-white hover:opacity-90",
                    )}
                  >
                    {isDisconnecting ? <CircleNotch size={12} weight="bold" className="animate-spin" /> : connected ? "Disconnect" : "Connect"}
                  </motion.button>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Citations section */}
      <section className="mb-8">
        <div className="flex items-center gap-2 mb-1.5">
          <BookOpen size={14} className="text-[var(--text-muted)]" />
          <h2 className="text-[13px] font-semibold text-[var(--text-primary)]">Citations</h2>
        </div>
        <p className="text-[11px] text-[var(--text-muted)] mb-3 pl-[22px]">
          Add a Semantic Scholar API key for higher search rate limits.{" "}
          <a
            href="https://www.semanticscholar.org/product/api#api-key-form"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-0.5 text-[var(--color-lever-orange)] hover:underline"
          >
            Request a free key
            <ArrowSquareOut size={10} weight="bold" />
          </a>
        </p>
        <div className="rounded-[var(--radius-lg)] border border-[var(--border-hairline)] bg-[var(--bg-surface)] px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={cn(
                "flex h-8 w-8 items-center justify-center rounded-[var(--radius-md)]",
                s2Key
                  ? "bg-[var(--color-signal-green)]/10 text-[var(--color-signal-green)]"
                  : "bg-[var(--bg-base)] text-[var(--text-muted)]",
              )}>
                <Key size={16} weight={s2Key ? "fill" : "regular"} />
              </div>
              <div>
                <div className="text-[13px] font-medium text-[var(--text-primary)]">Semantic Scholar</div>
                <div className="text-[11px] text-[var(--text-muted)]">
                  {s2Key ? `Key ending in ...${s2Key.slice(-4)}` : "Not configured — using shared rate limit"}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-1.5">
              {s2Key && !editingS2Key && (
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    setS2Key("");
                    toast.success("Key removed", "Semantic Scholar API key has been removed.");
                  }}
                  className="flex h-7 w-7 items-center justify-center rounded-[var(--radius-md)] border border-[var(--border-hairline)] text-[var(--text-muted)] hover:text-[var(--color-soft-red)] hover:border-[var(--color-soft-red)] transition-colors"
                  title="Remove key"
                >
                  <Trash size={12} />
                </motion.button>
              )}
              {!editingS2Key && (
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    setEditingS2Key(true);
                    setS2KeyInput("");
                    setTimeout(() => s2KeyInputRef.current?.focus(), 50);
                  }}
                  className={cn(
                    "rounded-[var(--radius-md)] px-3 py-1.5 text-[11px] font-medium transition-colors",
                    s2Key
                      ? "border border-[var(--border-subtle)] text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:border-[var(--border-default)]"
                      : "bg-[var(--text-primary)] text-[var(--text-inverse)] hover:opacity-90",
                  )}
                >
                  {s2Key ? "Replace" : "Add key"}
                </motion.button>
              )}
            </div>
          </div>

          <AnimatePresence>
            {editingS2Key && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.15 }}
                className="overflow-hidden"
              >
                <div className="flex items-center gap-2 mt-2.5">
                  <input
                    ref={s2KeyInputRef}
                    type="password"
                    value={s2KeyInput}
                    onChange={(e) => setS2KeyInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && s2KeyInput.trim()) {
                        setS2Key(s2KeyInput.trim());
                        toast.success("Key saved", "Semantic Scholar API key configured.");
                        setEditingS2Key(false);
                        setS2KeyInput("");
                      }
                      if (e.key === "Escape") {
                        setEditingS2Key(false);
                        setS2KeyInput("");
                      }
                    }}
                    placeholder="Paste your API key..."
                    className={cn(
                      "flex-1 rounded-[var(--radius-md)] border border-[var(--border-hairline)]",
                      "bg-[var(--bg-base)] px-2.5 py-1.5 text-[12px] text-[var(--text-primary)]",
                      "placeholder:text-[var(--text-muted)]",
                      "outline-none focus:border-[var(--border-subtle)]",
                      "transition-colors duration-100",
                    )}
                  />
                  <motion.button
                    whileTap={{ scale: 0.92 }}
                    disabled={!s2KeyInput.trim()}
                    onClick={() => {
                      if (!s2KeyInput.trim()) return;
                      setS2Key(s2KeyInput.trim());
                      toast.success("Key saved", "Semantic Scholar API key configured.");
                      setEditingS2Key(false);
                      setS2KeyInput("");
                    }}
                    className={cn(
                      "flex h-7 w-7 items-center justify-center rounded-[var(--radius-md)] transition-colors",
                      s2KeyInput.trim()
                        ? "bg-[var(--color-signal-green)]/10 text-[var(--color-signal-green)] hover:bg-[var(--color-signal-green)]/20"
                        : "bg-[var(--bg-base)] text-[var(--text-muted)]",
                    )}
                    title="Save key"
                  >
                    <Check size={12} weight="bold" />
                  </motion.button>
                  <motion.button
                    whileTap={{ scale: 0.92 }}
                    onClick={() => {
                      setEditingS2Key(false);
                      setS2KeyInput("");
                    }}
                    className="flex h-7 w-7 items-center justify-center rounded-[var(--radius-md)] bg-[var(--bg-base)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
                    title="Cancel"
                  >
                    <X size={12} weight="bold" />
                  </motion.button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* AI Providers (BYOK) section */}
      <section className="mb-8">
        <div className="flex items-center gap-2 mb-1.5">
          <Key size={14} className="text-[var(--text-muted)]" />
          <h2 className="text-[13px] font-semibold text-[var(--text-primary)]">AI Providers</h2>
        </div>
        <p className="text-[11px] text-[var(--text-muted)] mb-3 pl-[22px]">
          Optional — Lever provides default models. Add your own API key to use your account directly.
        </p>
        <div className="rounded-[var(--radius-lg)] border border-[var(--border-hairline)] bg-[var(--bg-surface)] divide-y divide-[var(--border-hairline)]">
          {AI_PROVIDERS.map(({ id, label, hint }) => {
            const existing = aiKeys.find((k) => k.provider === id);
            const isEditing = editingProvider === id;
            const isSaving = aiKeysSaving === id;

            return (
              <div key={id} className="px-4 py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "flex h-8 w-8 items-center justify-center rounded-[var(--radius-md)]",
                      existing
                        ? "bg-[var(--color-signal-green)]/10 text-[var(--color-signal-green)]"
                        : "bg-[var(--bg-base)] text-[var(--text-muted)]",
                    )}>
                      <Lightning size={16} weight={existing ? "fill" : "regular"} />
                    </div>
                    <div>
                      <div className="text-[13px] font-medium text-[var(--text-primary)]">{label}</div>
                      <div className="text-[11px] text-[var(--text-muted)]">
                        {existing ? `Key ending in ${existing.keyHint}` : "Not configured"}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5">
                    {existing && !isEditing && (
                      <motion.button
                        whileTap={{ scale: 0.95 }}
                        onClick={() => {
                          deleteAIKey(id);
                          toast.success("Key removed", `${label} API key has been removed.`);
                        }}
                        className="flex h-7 w-7 items-center justify-center rounded-[var(--radius-md)] border border-[var(--border-hairline)] text-[var(--text-muted)] hover:text-[var(--color-soft-red)] hover:border-[var(--color-soft-red)] transition-colors"
                        title="Remove key"
                      >
                        <Trash size={12} />
                      </motion.button>
                    )}
                    {!isEditing && (
                      <motion.button
                        whileTap={{ scale: 0.95 }}
                        onClick={() => {
                          setEditingProvider(id);
                          setKeyInput("");
                          setTimeout(() => keyInputRef.current?.focus(), 50);
                        }}
                        className={cn(
                          "rounded-[var(--radius-md)] px-3 py-1.5 text-[11px] font-medium transition-colors",
                          existing
                            ? "border border-[var(--border-subtle)] text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:border-[var(--border-default)]"
                            : "bg-[var(--text-primary)] text-[var(--text-inverse)] hover:opacity-90",
                        )}
                      >
                        {existing ? "Replace" : "Add key"}
                      </motion.button>
                    )}
                  </div>
                </div>

                <AnimatePresence>
                  {isEditing && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.15 }}
                      className="overflow-hidden"
                    >
                      <div className="flex items-center gap-2 mt-2.5">
                        <input
                          ref={keyInputRef}
                          type="password"
                          value={keyInput}
                          onChange={(e) => setKeyInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && keyInput.trim()) {
                              saveAIKey(id, keyInput.trim())
                                .then(() => {
                                  toast.success("Key saved", `${label} API key configured.`);
                                  setEditingProvider(null);
                                  setKeyInput("");
                                })
                                .catch(() => {
                                  toast.error("Invalid key", `Could not validate the ${label} API key.`);
                                });
                            }
                            if (e.key === "Escape") {
                              setEditingProvider(null);
                              setKeyInput("");
                            }
                          }}
                          placeholder={hint}
                          className={cn(
                            "flex-1 rounded-[var(--radius-md)] border border-[var(--border-hairline)]",
                            "bg-[var(--bg-base)] px-2.5 py-1.5 text-[12px] text-[var(--text-primary)]",
                            "placeholder:text-[var(--text-muted)]",
                            "outline-none focus:border-[var(--border-subtle)]",
                            "transition-colors duration-100",
                          )}
                        />
                        <motion.button
                          whileTap={{ scale: 0.92 }}
                          disabled={!keyInput.trim() || isSaving}
                          onClick={() => {
                            if (!keyInput.trim()) return;
                            saveAIKey(id, keyInput.trim())
                              .then(() => {
                                toast.success("Key saved", `${label} API key configured.`);
                                setEditingProvider(null);
                                setKeyInput("");
                              })
                              .catch(() => {
                                toast.error("Invalid key", `Could not validate the ${label} API key.`);
                              });
                          }}
                          className={cn(
                            "flex h-7 w-7 items-center justify-center rounded-[var(--radius-md)] transition-colors",
                            keyInput.trim()
                              ? "bg-[var(--color-signal-green)]/10 text-[var(--color-signal-green)] hover:bg-[var(--color-signal-green)]/20"
                              : "bg-[var(--bg-base)] text-[var(--text-muted)]",
                          )}
                          title="Save key"
                        >
                          {isSaving ? (
                            <CircleNotch size={12} weight="bold" className="animate-spin" />
                          ) : (
                            <Check size={12} weight="bold" />
                          )}
                        </motion.button>
                        <motion.button
                          whileTap={{ scale: 0.92 }}
                          onClick={() => {
                            setEditingProvider(null);
                            setKeyInput("");
                          }}
                          className="flex h-7 w-7 items-center justify-center rounded-[var(--radius-md)] bg-[var(--bg-base)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
                          title="Cancel"
                        >
                          <X size={12} weight="bold" />
                        </motion.button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </section>

      {/* Active Models section */}
      <section className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Cpu size={14} className="text-[var(--text-muted)]" />
            <h2 className="text-[13px] font-semibold text-[var(--text-primary)]">Active Models</h2>
          </div>
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => fetchModels()}
            className="flex items-center gap-1.5 rounded-[var(--radius-md)] px-2.5 py-1 text-[11px] font-medium text-[var(--text-muted)] hover:text-[var(--text-primary)] border border-[var(--border-hairline)] hover:border-[var(--border-subtle)] transition-colors"
          >
            <ArrowsClockwise size={11} weight="bold" />
            Refresh
          </motion.button>
        </div>
        {availableProviders.length === 0 ? (
          <div className="rounded-[var(--radius-lg)] border border-[var(--border-hairline)] bg-[var(--bg-surface)] px-4 py-6 text-center">
            <p className="text-[12px] text-[var(--text-muted)]">
              No models available. Add an API key above or set one in <code className="text-[11px] bg-[var(--bg-base)] px-1 py-0.5 rounded">backend/.env</code>, then restart the server.
            </p>
          </div>
        ) : (
          <div className="rounded-[var(--radius-lg)] border border-[var(--border-hairline)] bg-[var(--bg-surface)] divide-y divide-[var(--border-hairline)]">
            {availableProviders.map((provider) => (
              <div key={provider.id} className="px-4 py-3">
                <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] mb-2">
                  {provider.name}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {provider.models.map((model) => {
                    const isSelected =
                      selectedProvider === provider.id && selectedModel === model.id;
                    return (
                      <motion.button
                        key={model.id}
                        whileTap={{ scale: 0.97 }}
                        onClick={() => setProvider(provider.id, model.id)}
                        className={cn(
                          "flex items-center gap-1.5 rounded-[var(--radius-md)] px-2.5 py-1.5 text-[11px] font-medium transition-colors duration-100",
                          "border",
                          isSelected
                            ? "border-[var(--color-lever-orange)] bg-[var(--color-lever-orange)]/8 text-[var(--text-primary)]"
                            : "border-[var(--border-hairline)] text-[var(--text-secondary)] hover:border-[var(--border-subtle)] hover:text-[var(--text-primary)]",
                        )}
                      >
                        {model.name}
                        <span
                          className={cn(
                            "text-[9px] font-semibold uppercase tracking-wider",
                            model.tier === "free"
                              ? "text-[var(--color-signal-green)]"
                              : "text-[var(--color-ember-orange)]",
                          )}
                        >
                          {model.tier}
                        </span>
                        {isSelected && (
                          <Check size={10} weight="bold" className="text-[var(--color-lever-orange)]" />
                        )}
                      </motion.button>
                    );
                  })}
                </div>
              </div>
            ))}
            {selectedProvider && selectedModel && (
              <div className="px-4 py-2.5 bg-[var(--bg-base)]">
                <span className="text-[10px] text-[var(--text-muted)]">
                  Primary model:{" "}
                  <span className="font-medium text-[var(--text-secondary)]">
                    {availableProviders
                      .flatMap((p) => p.models.map((m) => ({ ...m, provider: p.name })))
                      .find((m) => m.id === selectedModel)?.name ?? selectedModel}
                  </span>
                  {" — "}used for chat, edits, and explanations
                </span>
              </div>
            )}
          </div>
        )}
      </section>

      {/* AI Completion section */}
      <section className="mb-8">
        <div className="flex items-center gap-2 mb-3">
          <CursorText size={14} className="text-[var(--text-muted)]" />
          <h2 className="text-[13px] font-semibold text-[var(--text-primary)]">AI Completion</h2>
        </div>
        <div className="rounded-[var(--radius-lg)] border border-[var(--border-hairline)] bg-[var(--bg-surface)] p-4 space-y-5">
          {/* Enable / Disable */}
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[13px] font-medium text-[var(--text-primary)]">Ghost text completions</div>
              <div className="text-[11px] text-[var(--text-muted)]">AI predicts what you'll type next</div>
            </div>
            <motion.button
              onClick={() => setCompletionEnabled(!completionEnabled)}
              whileTap={{ scale: 0.95 }}
              className={cn(
                "relative h-5 w-9 rounded-full transition-colors duration-150",
                completionEnabled ? "bg-[var(--color-lever-orange)]" : "bg-[var(--border-default)]",
              )}
            >
              <motion.div
                className="absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow-sm"
                animate={{ x: completionEnabled ? 16 : 0 }}
                transition={{ type: "spring", stiffness: 500, damping: 35 }}
              />
            </motion.button>
          </div>

          {completionEnabled && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="space-y-4"
            >
              {/* Completion model */}
              <div>
                <div className="text-[12px] font-medium text-[var(--text-secondary)] mb-1">Model</div>
                <p className="text-[10px] text-[var(--text-muted)] mb-2">
                  Use a lightweight model to save API quota. Falls back to your primary model if unset.
                </p>
                <CompletionModelPicker
                  providers={availableProviders}
                  completionProvider={completionProvider}
                  completionModel={completionModel}
                  onSelect={setCompletionProvider}
                  onClear={clearCompletionProvider}
                />
              </div>

              {/* Aggressiveness */}
              <div>
                <div className="text-[12px] font-medium text-[var(--text-secondary)] mb-2">Speed</div>
                <SegmentedControl
                  options={AGGRESSIVENESS_OPTIONS}
                  value={completionAggressiveness}
                  onChange={setCompletionAggressiveness}
                />
              </div>

              {/* Max length */}
              <div>
                <div className="text-[12px] font-medium text-[var(--text-secondary)] mb-2">Completion length</div>
                <SegmentedControl
                  options={LENGTH_OPTIONS}
                  value={completionMaxLength}
                  onChange={setCompletionMaxLength}
                />
              </div>

              {/* Trigger mode */}
              <div>
                <div className="text-[12px] font-medium text-[var(--text-secondary)] mb-2">Trigger</div>
                <SegmentedControl
                  options={TRIGGER_OPTIONS}
                  value={completionTrigger}
                  onChange={setCompletionTrigger}
                />
              </div>

              {/* Keyboard shortcuts hint */}
              <div className="rounded-[var(--radius-md)] bg-[var(--bg-base)] px-3 py-2.5 text-[11px] text-[var(--text-muted)] space-y-1">
                <div className="flex justify-between"><span>Accept full suggestion</span><kbd className="text-[10px] font-mono">Tab</kbd></div>
                <div className="flex justify-between"><span>Accept next word</span><kbd className="inline-flex items-center gap-0.5 text-[10px] font-mono">{KbdCmd}{KbdRight}</kbd></div>
                <div className="flex justify-between"><span>Accept next line</span><kbd className="inline-flex items-center gap-0.5 text-[10px] font-mono">{KbdShift}Tab</kbd></div>
                <div className="flex justify-between"><span>Dismiss</span><kbd className="text-[10px] font-mono">Esc</kbd></div>
              </div>
            </motion.div>
          )}
        </div>
      </section>

      {/* Keyboard shortcuts section */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <Keyboard size={14} className="text-[var(--text-muted)]" />
          <h2 className="text-[13px] font-semibold text-[var(--text-primary)]">Keyboard shortcuts</h2>
        </div>
        <div className="rounded-[var(--radius-lg)] border border-[var(--border-hairline)] bg-[var(--bg-surface)] overflow-hidden">
          {shortcutGroups.map((group) => (
            <div key={group.title}>
              <div className="px-4 py-2 text-[10px] font-bold uppercase tracking-[0.08em] text-[var(--text-muted)] bg-[var(--bg-base)]">
                {group.title}
              </div>
              {group.items.map((item, i) => (
                <div
                  key={i}
                  className={cn(
                    "flex items-center justify-between px-4 py-2.5",
                    i < group.items.length - 1 && "border-b border-[var(--border-hairline)]",
                  )}
                >
                  <span className="text-[12px] text-[var(--text-secondary)]">{item.label}</span>
                  <div className="flex items-center gap-1">
                    {item.keys.map((key, ki) => (
                      <kbd
                        key={ki}
                        className="inline-flex items-center justify-center min-w-[20px] text-center rounded border border-[var(--border-hairline)] bg-[var(--bg-base)] px-1.5 py-0.5 text-[10px] font-medium text-[var(--text-muted)]"
                      >
                        {key}
                      </kbd>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
